/**
 * User List Component
 * 
 * Displays a list of users/staff with search, filtering, and management capabilities.
 * Uses reusable components for toolbar, status toggle, and data display.
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import StickyFlexTable from '../common/StickyFlexTable';
import ThemedTooltip from '../common/ThemedTooltip';
import ThemedChip from '../common/ThemedChip';
import ManagementToolbar from '../common/ManagementToolbar';
import StatusToggleButton from '../common/StatusToggleButton';
import StatusToggleDialog from '../common/StatusToggleDialog';
import { useTheme } from '../../ThemeContext';
import IconButton from '@mui/material/IconButton';
import { formatPhoneNumberForList } from '../../utils/helpers/format-phone-number';
import { useDetectMyRole, useStatusToggle } from '../../custom-hooks';
import UserService from '../../services/user-service';
import { 
  USER_FILTER_GROUPS, 
  SEARCH_PLACEHOLDERS, 
  MODULE_PERMISSIONS 
} from '../../constants/filter-constants';

/**
 * UserList Component
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onAddNew - Add new user handler
 * @param {Function} props.onEdit - Edit user handler
 * @param {Function} props.onRefresh - Refresh handler
 * @param {Array} props.users - Array of user objects
 * @param {boolean} props.loading - Loading state
 * @param {Array} props.facilities - Array of facility objects
 * @param {Object} props.selectedFilters - Current filter state
 * @param {Function} props.onFiltersChange - Filter change handler
 * @returns {JSX.Element} UserList component
 */
const UserList = ({ 
  onAddNew, 
  onEdit, 
  onRefresh, 
  users, 
  loading, 
  facilities,
  selectedFilters,
  onFiltersChange,
  // Legacy props (kept for compatibility)
  onDelete
}) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { currentTheme } = useTheme();
  const { role } = useDetectMyRole();

  // Use status toggle hook
  const {
    isDialogOpen: statusDialogOpen,
    isLoading: statusLoading,
    entityToToggle: userToToggle,
    toggleAction,
    handleToggleClick,
    handleConfirm: handleStatusToggleConfirm,
    handleCancel: handleStatusToggleCancel
  } = useStatusToggle(UserService.toggleStatus, onRefresh, 'user');

  // Check permissions
  const canEditUser = MODULE_PERMISSIONS.user.edit.includes(role);

  /**
   * Reset pagination to first page when filters or search query change
   * This ensures user doesn't stay on a page that no longer exists
   */
  useEffect(() => {
    setPage(0);
  }, [selectedFilters, query]);

  /**
   * Creates a map of facility IDs to facility names
   * 
   * @returns {Object} Facility ID to name mapping
   */
  const createFacilityMap = () => {
    return facilities.reduce((acc, facility) => {
      acc[facility._id] = facility.facilityName;
      return acc;
    }, {});
  };

  const facilityMap = createFacilityMap();

  /**
   * Apply filters to users based on selected filters
   * 
   * @param {Array} usersData - Raw users data
   * @returns {Array} Filtered users
   */
  const applyFilters = (usersData) => {
    return usersData.filter((user) => {
      // Status filter
      const statusFilter = selectedFilters?.status;
      if (statusFilter === 'active' && user.isActive !== true) return false;
      if (statusFilter === 'inactive' && user.isActive !== false) return false;
      
      // Search filter
      if (query && !user.name.toLowerCase().includes(query.toLowerCase())) return false;
      
      return true;
    });
  };

  const filteredUsersData = applyFilters(users);

  /**
   * Renders user facilities as chips
   * 
   * @param {Array} facilityIds - Array of facility IDs
   * @returns {JSX.Element} Rendered facility chips
   */
  const renderFacilities = (facilityIds) => {

    
    if (!Array.isArray(facilityIds) || facilityIds.length === 0) {
      return <span style={{ fontSize: 13, opacity: 0.7 }}>No facilities assigned</span>;
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {facilityIds.map((facilityId) => (
          <ThemedChip
            key={facilityId}
            variant="default"
            size="small"
          >
            {facilityMap[facilityId] || 'Unknown Facility'}
          </ThemedChip>
        ))}
      </Box>
    );
  };

  /**
   * Renders user email with truncation for long emails
   * 
   * @param {string} email - User email
   * @returns {JSX.Element} Rendered email with tooltip
   */
  const renderEmail = (email) => {
    const MAX_EMAIL_LENGTH = 24;
    
    if (!email) {
      return <span style={{ fontSize: 13, opacity: 0.7 }}>No email</span>;
    }

    // If email is longer than max length, truncate and show tooltip
    if (email.length > MAX_EMAIL_LENGTH) {
      const truncatedEmail = email.substring(0, MAX_EMAIL_LENGTH) + '...';
      
      return (
        <ThemedTooltip 
          title={email} 
          placement="top"
        >
          <span style={{ cursor: 'pointer' }}>
            {truncatedEmail}
          </span>
        </ThemedTooltip>
      );
    }

    // Email is short enough, render as-is
    return <span>{email}</span>;
  };

  /**
   * Renders user role
   * 
   * @param {string|Array} role - User role(s)
   * @returns {string} Formatted role string
   */
  const renderRole = (role) => {
    return Array.isArray(role) ? role.join(', ') : role;
  };

  // Table columns configuration
  const columns = [
    {
      renderName: 'name',
      headerName: 'Name',
      width: 200,
      fixed: true,
    },
    {
      renderName: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (row) => renderEmail(row.email),
    },
    {
      renderName: 'phoneNumber',
      headerName: 'Phone Number',
      minWidth: 180,
      renderCell: (row) => formatPhoneNumberForList(row.phoneNumber),
    },
    {
      renderName: 'role',
      headerName: 'Role',
      width: 100,
      renderCell: (row) => renderRole(row.role),
    },
    {
      renderName: 'facilities',
      headerName: 'Facilities',
      minWidth: 200,
      renderCell: (row) => renderFacilities(row.facilityIds),
    },
    {
      renderName: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (row) => (
        <ThemedChip
          variant={row.isActive ? "success" : "warning"}
          size="small"
        >
          {row.isActive ? "Active" : "Inactive"}
        </ThemedChip>
      ),
    },
    {
      id: "action",
      label: "Action",
      minWidth: 120,
      fixed: true,
      align: "right",
      renderCell: (row) => (
        <>
          <ThemedTooltip 
            title={
              canEditUser 
                ? "Edit user information" 
                : "Administrator privileges required to modify user records"
            }
            placement="top"
          >
            <span>
              <IconButton
                aria-label="edit"
                onClick={() => canEditUser && onEdit && onEdit(row)}
                size="small"
                disabled={!canEditUser}
                sx={{ 
                  color: canEditUser 
                    ? currentTheme.text.primary 
                    : currentTheme.text.disabled || '#999',
                  cursor: canEditUser ? 'pointer' : 'not-allowed'
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </ThemedTooltip>
          <StatusToggleButton
            entity={row}
            module="user"
            onToggle={handleToggleClick}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <StickyFlexTable
        columns={columns}
        rows={filteredUsersData}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        toolbar={
          <ManagementToolbar
            module="user"
            searchQuery={query}
            onSearchChange={setQuery}
            searchPlaceholder={SEARCH_PLACEHOLDERS.user}
            filterGroups={USER_FILTER_GROUPS}
            selectedFilters={selectedFilters}
            onFiltersChange={onFiltersChange}
            onAdd={onAddNew}
            addButtonLabel="Add User"
          />
        }
        containerHeight="62vh"
      />

      {/* Status Toggle Dialog */}
      <StatusToggleDialog
        open={statusDialogOpen}
        entity={userToToggle}
        action={toggleAction}
        module="user"
        onConfirm={handleStatusToggleConfirm}
        onCancel={handleStatusToggleCancel}
        loading={statusLoading}
      />
    </>
  );
};

export default UserList;